import {
  Accessor,
  Component,
  For,
  Index,
  Setter,
  Show,
  createEffect,
  createMemo,
  createSignal,
  on,
  onMount,
} from 'solid-js'
import {
  NOVEL_IMAGE_MODEL,
  NOVEL_SAMPLER_REV,
  SD_SAMPLER,
  SD_SAMPLER_REV,
  SWARM_SAMPLER,
  SWARM_SAMPLER_REV,
} from '../../../../common/image'
import Select from '../../../shared/Select'
import TextInput from '../../../shared/TextInput'
import { i18nStore, pageStore, settingStore, userStore } from '../../../store'
import { ImageProviderLora, ImageSettings } from '/common/types/image-schema'
import { SetStoreFunction } from 'solid-js/store'
import { applyStoreProperty, createEmitter } from '/web/shared/util'
import { Toggle } from '/web/shared/Toggle'
import Button, { ToggleButton } from '/web/shared/Button'
import { Info, RefreshCcw, X } from 'lucide-solid'
import { Card, Pill } from '/web/shared/Card'
import { InlineRangeInput } from '/web/shared/RangeInput'
import { useProviderList } from '../Provider/hooks'
import { CustomOption, CustomSelect } from '/web/shared/CustomSelect'
import { imageApi } from '/web/store/data/image'

export const NovelSettings: Component<{
  cfg: ImageSettings
  setter: SetStoreFunction<ImageSettings>
}> = (props) => {
  const i18n = i18nStore((s) => s)
  const state = userStore((s) => ({ user: s.user }))

  const isKeySet = createMemo(() => {
    const provider = state.user?.providers?.find((p) => p.provider === 'known-novel')

    const isSet = !!provider?.keySet || !!state.user?.novelApiKey
    return isSet
  })

  const models = createMemo(() => Object.entries(NOVEL_IMAGE_MODEL).map(([key, value]) => ({
    label: `${i18n.t.service_settings_model} ${key}`,
    value,
  })))
  const samplers = createMemo(() => Object.entries(NOVEL_SAMPLER_REV).map(([key, value]) => ({
    label: `${i18n.t.service_settings_sampler} ${value}`,
    value: key,
  })))
  return (
    <>
      <Show when={!isKeySet()}>
        <div class="font-bold text-red-600">
          {i18n.t.service_settings_novel_key_error}
        </div>
      </Show>

      <Toggle
        fieldName="novelQualityTags"
        label={i18n.t.service_settings_novel_quality_tags}
        value={props.cfg.novel.qualityTags}
        onChange={(ev) => props.setter(applyStoreProperty(props.cfg, 'novel.qualityTags', ev))}
      />

      <Select
        fieldName="novelUndesiredContent"
        label={i18n.t.service_settings_novel_undesired_content}
        helperMarkdown={i18n.t.service_settings_novel_undesired_content_helper}
        inline
        class="!py-1"
        items={[
          { label: i18n.t.service_settings_novel_heavy, value: '0' },
          { label: i18n.t.service_settings_novel_light, value: '1' },
          { label: i18n.t.service_settings_novel_none, value: '2' },
        ]}
        value={props.cfg.novel.ucPreset || '0'}
        onChange={(ev) => props.setter(applyStoreProperty(props.cfg, 'novel.ucPreset', ev.value))}
      />

      <Select
        fieldName="novelImageModel"
        items={models()}
        inline
        class="!py-1"
        value={props.cfg?.novel?.model}
        onChange={(ev) => props.setter(applyStoreProperty(props.cfg, 'novel.model', ev.value))}
      />
      <Select
        fieldName="novelSampler"
        items={samplers()}
        inline
        class="!py-1"
        value={props.cfg?.novel?.sampler || NOVEL_SAMPLER_REV.k_dpmpp_2m}
        onChange={(ev) => props.setter(applyStoreProperty(props.cfg, 'novel.sampler', ev.value))}
      />
    </>
  )
}

export const HordeSettings: Component<{
  cfg: ImageSettings
  setter: SetStoreFunction<ImageSettings>
}> = (props) => {
  const i18n = i18nStore((s) => s)
  const cfg = settingStore((s) => ({ imageWorkers: s.imageWorkers }))

  const models = createMemo(() => {
    const map = new Map<string, number>()

    for (const worker of cfg.imageWorkers) {
      for (const model of worker.models) {
        if (!map.has(model)) {
          map.set(model, 0)
        }

        const current = map.get(model) ?? 0
        map.set(model, current + 1)
      }
    }

    const items = Array.from(map.entries())
      .sort(([, l], [, r]) => (l > r ? -1 : l === r ? 0 : 1))
      .map(([name, count]) => ({
        label: `${i18n.t.service_settings_model} ${name} (${count})`,
        value: name,
      }))
    return items
  })

  createEffect(() => {
    settingStore.getHordeImageWorkers()
    settingStore.getImageLoras()
  })

  const samplers = createMemo(() => Object.entries(SD_SAMPLER_REV).map(([key, value]) => ({
    label: `${i18n.t.service_settings_sampler} ${value}`,
    value: key,
  })))
  return (
    <>
      <Select
        fieldName="hordeImageModel"
        items={models()}
        inline
        class="!py-1"
        value={props.cfg.horde?.model || 'stable_diffusion'}
        onChange={(ev) => props.setter(applyStoreProperty(props.cfg, 'horde.model', ev.value))}
      />
      <Select
        fieldName="hordeSampler"
        items={samplers()}
        class="!py-1"
        inline
        value={props.cfg.horde?.sampler || SD_SAMPLER['DPM++ 2M']}
        onChange={(ev) => props.setter(applyStoreProperty(props.cfg, 'horde.sampler', ev.value))}
      />
    </>
  )
}

export const SwarmSettings: Component<{
  cfg: ImageSettings
  setter: SetStoreFunction<ImageSettings>
}> = (props) => {
  const i18n = i18nStore((s) => s)
  const emitter = createEmitter('open')
  const [models, setModels] = createSignal<CustomOption[]>([])
  const [loraList, setLoraList] = createSignal<LoraItem[]>([])
  const loras = useLoras(props.setter, 'swarm.loras', props.cfg.swarm?.loras)

  const loadModels = async () => {
    if (props.cfg.type !== 'swarm') return
    const result = await imageApi.getImageModelList({
      type: 'swarm',
      local: props.cfg.swarm.local,
      url: props.cfg.swarm?.url,
    })

    const options = result.models.map((model) => ({
      label: model.title,
      value: model.title,
    }))
    if ('loras' in result) {
      const opts = result.loras.map<LoraItem>((model) => ({
        id: model.title,
        name: model.title,
        tags: model.tags?.reduce(
          (prev, curr, i, all) => Object.assign(prev, { [curr]: all.length - i }),
          {} as Record<string, number>
        ),
      }))
      setLoraList(opts)
    } else {
      setLoraList([])
    }

    options.unshift({ label: i18n.t.service_settings_automatic, value: '' })
    setModels(options)
  }

  onMount(() => {
    emitter.on('open', loadModels)
  })

  const samplers = createMemo(() => Object.entries(SWARM_SAMPLER_REV).map(([key, value]) => ({
    label: `${i18n.t.service_settings_sampler} ${value}`,
    value: key,
  })))

  return (
    <>
      <TextInput
        fieldName="sdUrl"
        label={i18n.t.service_settings_swarm_url}
        helperText={i18n.t.service_settings_swarm_url_helper}
        placeholder={i18n.t.service_settings_swarm_url_helper}
        value={props.cfg.swarm?.url || 'http://localhost:7801'}
        onChange={(ev) =>
          props.setter(
            applyStoreProperty(props.cfg, 'swarm.url', ev.currentTarget.value)
          )
        }
      />

      <div class="flex items-center gap-1">
        <CustomSelect
          listener={emitter.emit}
          onSelect={(ev) =>
            props.setter(applyStoreProperty(props.cfg, 'swarm.model', ev.value))
          }
          selected={props.cfg.swarm?.model}
          options={models()}
          buttonLabel={props.cfg.swarm?.model || i18n.t.service_settings_automatic}
          autoSearch={true}
        />

        <Show when={loraList().length}>
          <Button size="sm" onClick={() => loras.add(props.cfg)}>
            {i18n.t.service_settings_add_lora}
          </Button>
        </Show>

        <div class="icon-button" onClick={loadModels}>
          <RefreshCcw />
        </div>
      </div>

      <LoraSelector cfg={props.cfg} hook={loras} items={loraList()} />

      <Select
        fieldName="swarmSampler"
        items={samplers()}
        inline
        class="!py-1"
        value={props.cfg.swarm?.sampler || SWARM_SAMPLER['Euler a']}
        onChange={(ev) =>
          props.setter(applyStoreProperty(props.cfg, 'swarm.sampler', ev.value))
        }
      />

      <Toggle
        label={i18n.t.service_settings_use_local}
        value={props.cfg.swarm?.local ?? false}
        onChange={(ev) =>
          props.setter(applyStoreProperty(props.cfg, 'swarm.local', ev))
        }
      />
    </>
  )
}

export const SDSettings: Component<{
  cfg: ImageSettings
  setter: SetStoreFunction<ImageSettings>
}> = (props) => {
  const i18n = i18nStore((s) => s)
  const emitter = createEmitter('open')
  const [providers] = useProviderList(true)
  const [models, setModels] = createSignal<CustomOption[]>([])
  const providerLabel = createMemo(() => {
    if (!props.cfg?.sd?.providerId) return i18n.t.service_settings_none_selected

    const selected = providers().find((p) => p.value === props.cfg.sd.providerId)
    if (!selected) return i18n.t.service_settings_none_selected

    return selected.label
  })

  const loadModels = async () => {
    if (!props.cfg.sd.url) return
    const result = await imageApi.getSDModelList({
      url: props.cfg.sd.url,
      providerId: props.cfg.sd.providerId,
    })

    const options = result.models.map((model) => ({
      label: model.title,
      value: model.title,
    }))
    options.unshift({ label: i18n.t.service_settings_automatic, value: '' })
    setModels(options)
  }

  onMount(() => {
    emitter.on('open', loadModels)
  })

  const samplers = createMemo(() => Object.entries(SD_SAMPLER_REV).map(([key, value]) => ({
    label: `${i18n.t.service_settings_sampler} ${value}`,
    value: key,
  })))

  return (
    <>
      <TextInput
        fieldName="sdUrl"
        label={i18n.t.service_settings_sd_url}
        helperText={i18n.t.service_settings_sd_url_helper}
        placeholder={i18n.t.service_settings_sd_url_helper}
        value={props.cfg.sd?.url}
        onChange={(ev) =>
          props.setter(applyStoreProperty(props.cfg, 'sd.url', ev.currentTarget.value))
        }
      />

      <CustomSelect
        onSelect={(ev) =>
          props.setter(applyStoreProperty(props.cfg, 'sd.providerId', ev.value))
        }
        selected={props.cfg?.sd?.providerId}
        helperText={i18n.t.service_settings_sd_provider_helper}
        options={providers()}
        buttonLabel={providerLabel()}
      />

      <div class="flex items-center gap-1">
        <CustomSelect
          listener={emitter.emit}
          onSelect={(ev) =>
            props.setter(applyStoreProperty(props.cfg, 'sd.model', ev.value))
          }
          selected={props.cfg.sd.model}
          options={models()}
          buttonLabel={props.cfg.sd.model || i18n.t.service_settings_automatic}
          autoSearch={true}
        />
        <div class="icon-button" onClick={loadModels}>
          <RefreshCcw />
        </div>
      </div>

      <Select
        fieldName="sdSampler"
        items={samplers()}
        inline
        class="!py-1"
        value={props.cfg.sd?.sampler || SD_SAMPLER['DPM++ 2M']}
        onChange={(ev) =>
          props.setter(applyStoreProperty(props.cfg, 'sd.sampler', ev.value))
        }
      />
    </>
  )
}

export const AgnaiSettings: Component<{
  cfg: ImageSettings
  setter: SetStoreFunction<ImageSettings>
}> = (props) => {
  const i18n = i18nStore((s) => s)
  const settings = settingStore((s) => {
    const models = s.config.serverConfig?.imagesModels || []
    return {
      loras: s.loras,
      embeddings: s.embeddings,
      models,
      names: models.map((m) => ({
        label: `${i18n.t.service_settings_model} ${m.desc.trim()}`,
        value: m.id || m.name,
      })),
    }
  })

  const lhook = useLoras(props.setter, 'agnai.loras', props.cfg.agnai.loras)

  const loraList = createMemo(() => {
    const list = lhook.loras().map((lora) => {
      const tags = settings.loras.find((l) => l.id === lora.id)
      if (!tags) return { ...lora, name: lora.id, tags: undefined }
      return { ...lora, id: lora.id, name: lora.id, tags: tags.tags }
    })

    return list
  })

  const model = createMemo(() => {
    const original = props.cfg.agnai?.model
    const id =
      settings.models.length === 1
        ? settings.models[0].id || settings.models[0].name
        : props.cfg.agnai?.model || original
    const match = settings.models.find((m) => m.id === id || m.name === id)
    return match
  })

  const samplers = createMemo(() => {
    return Object.entries(SD_SAMPLER_REV).map(([key, value]) => ({
      label: `${i18n.t.service_settings_sampler} ${value}`,
      value: key,
    }))
  })
  createEffect(
    on(
      () => props.cfg.agnai.loras,
      () => {
        const prev = lhook.loras()
        if (prev.length !== props.cfg.agnai.loras?.length) return
        lhook.setLoras(props.cfg.agnai.loras || [])
      }
    )
  )

  return (
    <>
      <Show when={settings.models.length === 0}>
        <i>{i18n.t.service_settings_no_options}</i>
      </Show>
      <div class="flex items-end gap-2">
        <Select
          fieldName="agnaiModel"
          label={i18n.t.service_settings_image_model}
          items={settings.names}
          value={props.cfg.agnai?.model || settings.names[0]?.value}
          disabled={settings.models.length <= 1}
          classList={{ hidden: settings.models.length === 0 }}
          onChange={(ev) =>
            props.setter(applyStoreProperty(props.cfg, 'agnai.model', ev.value))
          }
        />
        <Show when={settings.loras.length}>
          <Button onClick={() => lhook.add(props.cfg)}>
            {i18n.t.service_settings_add_lora}
          </Button>
        </Show>
      </div>

      <LoraSelector hook={lhook} cfg={props.cfg} items={loraList()} />

      <Select
        fieldName="agnaiSampler"
        items={samplers()}
        label={i18n.t.service_settings_sampler_label}
        value={props.cfg.agnai?.sampler}
        onChange={(ev) =>
          props.setter(applyStoreProperty(props.cfg, 'agnai.sampler', ev.value))
        }
      />

      <Toggle
        label={i18n.t.service_settings_draft_mode}
        helperText={i18n.t.service_settings_draft_mode_helper}
        value={props.cfg.agnai?.draftMode}
        onChange={(ev) =>
          props.setter(applyStoreProperty(props.cfg, 'agnai.draftMode', ev))
        }
      />

      <Show when={!!model()}>
        <div>
          <table class="table-auto border-separate border-spacing-1 text-sm ">
            <thead>
              <tr>
                <Th />
                <Th>{i18n.t.service_settings_steps}</Th>
                <Th>{i18n.t.service_settings_cfg}</Th>
                <Th>{i18n.t.service_settings_width}</Th>
                <Th>{i18n.t.service_settings_height}</Th>
                <Th>{i18n.t.service_settings_clip_skip}</Th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <Td class="bg-700 !border-0">{i18n.t.service_settings_recommended}</Td>
                <Td>{model()?.init.steps}</Td>
                <Td>{model()?.init.cfg}</Td>
                <Td>{model()?.init.width}</Td>
                <Td>{model()?.init.height}</Td>
                <Td>{model()?.init.clipSkip || ''}</Td>
              </tr>

              <tr>
                <Td class="bg-700 !border-0">{i18n.t.service_settings_limits}</Td>
                <Td>{model()?.limit.steps}</Td>
                <Td>{model()?.limit.cfg}</Td>
                <Td>{model()?.limit.width}</Td>
                <Td>{model()?.limit.height}</Td>
                <Td>{model()?.limit.clipSkip || ''}</Td>
              </tr>

              <Show when={model()?.init.sampler}>
                <tr>
                  <Td class="bg-700 !border-0">{i18n.t.service_settings_sampler_title}</Td>
                  <Td span={5}>
                    {(SD_SAMPLER_REV as any)[model()?.init.sampler!]}
                  </Td>
                </tr>
              </Show>

              <tr>
                <Td class="bg-700 !border-0">{i18n.t.service_settings_prefix}</Td>
                <Td span={5}>{model()?.init.prefix}</Td>
              </tr>

              <tr>
                <Td class="bg-700 !border-0">{i18n.t.service_settings_suffix}</Td>
                <Td span={5}>{model()?.init.suffix}</Td>
              </tr>

              <tr>
                <Td class="bg-700 !border-0">{i18n.t.service_settings_negative}</Td>
                <Td span={5}>{model()?.init.negative}</Td>
              </tr>
            </tbody>
          </table>
        </div>
      </Show>
    </>
  )
}

const Th: Component<{ children?: any }> = (props) => (
  <th
    class="rounded-md border-[var(--bg-600)] px-2 font-bold"
    classList={{ border: !!props.children, 'bg-[var(--bg-700)]': !!props.children }}
  >
    {props.children}
  </th>
)
const Td: Component<{ children?: any; span?: number; class?: string }> = (props) => (
  <td
    class={`rounded-md border-[var(--bg-700)] px-2 ${props.class || ''}`}
    colSpan={props.span}
    classList={{ border: !!props.children }}
  >
    {props.children}
  </td>
)

type LoraItem = { id: string; name: string; tags?: Record<string, number> }
type LoraHook = {
  loras: Accessor<ImageProviderLora[]>
  setLoras: Setter<ImageProviderLora[]>
  add: (cfg: ImageSettings) => void
  remove: (cfg: ImageSettings, i: number) => void
  update: (cfg: ImageSettings, i: number, update: Partial<ImageProviderLora>) => void
}

function useLoras(
  setter: SetStoreFunction<ImageSettings>,
  property: string,
  initial: ImageProviderLora[] | undefined
): LoraHook {
  const [loras, setLoras] = createSignal(initial || [])

  const addLora = (cfg: ImageSettings) => {
    const next = loras().concat({
      id: '',
      clipStrength: 1.0,
      modelStrength: 1.0,
      enabled: true,
    })
    setter(applyStoreProperty(cfg, property, next))
    setLoras(next)
  }

  const removeLora = (cfg: ImageSettings, i: number) => {
    const next = loras().filter((_, index) => index !== i)
    setter(applyStoreProperty(cfg, property, next))
    setLoras(next)
  }

  const updateLora = (
    cfg: ImageSettings,
    index: number,
    update: Partial<{ id: string; clipStrength: number; modelStrength: number; enabled?: boolean }>
  ) => {
    const next = loras().map((lora, i) => {
      if (i !== index) return lora
      return { ...lora, ...update }
    })

    setLoras(next)
    setter(applyStoreProperty(cfg, property, next))
  }

  return { loras, setLoras, add: addLora, remove: removeLora, update: updateLora }
}

const LoraSelector: Component<{
  hook: LoraHook
  items: LoraItem[]
  cfg: ImageSettings
}> = (props) => {
  const i18n = i18nStore((s) => s)
  const availableLoras = createMemo(() => {
    const list = props.items
      .filter((lora) => {
        if (!props.items.length) return lora
        return true
        // if (used) return false
      })
      .map((lora) => ({ label: lora.name, value: lora.id }))

    return [{ label: i18n.t.service_settings_none_selected, value: '' }].concat(list)
  })

  const loraList = createMemo(() => {
    const list = props.hook.loras().map((lora) => {
      const tags = props.items.find((l) => l.id === lora.id)
      if (!tags) return { ...lora, tags: undefined }
      return { ...lora, tags: tags.tags }
    })

    return list
  })

  const loraHelp = (tags?: Record<string, number>) => {
    if (!tags) return

    const list = Object.entries(tags)
      .map(([key, value]) => ({ tag: key, count: value }))
      .sort((l, r) => r.count - l.count)

    const content = (
      <div class="flex flex-col">
        <div class="bold">{i18n.t.service_settings_tags_keyword}</div>
        <div class="flex flex-wrap gap-1">
          <For each={list}>
            {(tag) => (
              <Pill small>
                <span class="bold">{tag.tag}</span>: <span>{tag.count}</span>
              </Pill>
            )}
          </For>
        </div>
      </div>
    )

    pageStore.openConfirm({ message: content })
  }

  return (
    <Index each={loraList()}>
      {(lora, i) => (
        <Card class="flex flex-col gap-1">
          <div class="flex items-center gap-1">
            <div class="icon-button" onClick={() => props.hook.remove(props.cfg, i)}>
              <X size={20} />
            </div>
            <CustomSelect
              onSelect={(item) => props.hook.update(props.cfg, i, { id: item.value })}
              selected={lora().id}
              options={availableLoras()}
              buttonLabel={lora().id || i18n.t.service_settings_none_selected}
              autoSearch={true}
            />

            <Button size="sm" schema="hollow" onClick={() => loraHelp(lora().tags)}>
              {i18n.t.service_settings_tags} <Info size={16} />
            </Button>

            <ToggleButton
              size="sm"
              onChange={(ev) => props.hook.update(props.cfg, i, { enabled: ev })}
              value={lora().enabled}
            >
              {lora().enabled ? i18n.t.service_settings_on : i18n.t.service_settings_off}
            </ToggleButton>
          </div>
          <InlineRangeInput
            fieldName="modelStrength"
            label={i18n.t.service_settings_model_strength}
            min={0}
            max={2}
            parentClass="text-sm"
            value={lora().modelStrength}
            onChange={(ev) => props.hook.update(props.cfg, i, { modelStrength: +ev })}
            step={0.05}
          />
          <InlineRangeInput
            fieldName="clipStrength"
            label={i18n.t.service_settings_clip_strength}
            min={0}
            max={2}
            parentClass="text-sm"
            value={lora().clipStrength}
            onChange={(ev) => props.hook.update(props.cfg, i, { clipStrength: +ev })}
            step={0.05}
          />
        </Card>
      )}
    </Index>
  )
}
